import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

// Automatically load every JPG / JPEG image inside src/Assets
const imageContext = require.context(
  "../Assets",
  false,
  /\.(jpg|jpeg)$/i
);

const assetImages = imageContext
  .keys()
  .sort((a, b) =>
    a.localeCompare(b, undefined, {
      numeric: true,
      sensitivity: "base",
    })
  )
  .map((path) => ({
    src: imageContext(path),
    name: path.replace("./", ""),
  }));

const PhotoGallery = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.05,
  });

  const images = useMemo(() => assetImages, []);

  useEffect(() => {
    if (images.length === 0 || isPaused) return undefined;

    const interval = setInterval(() => {
      setCurrentIndex(
        (previousIndex) => (previousIndex + 1) % images.length
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [images.length, isPaused]);

  useEffect(() => {
    if (currentIndex >= images.length && images.length > 0) {
      setCurrentIndex(0);
    }
  }, [currentIndex, images.length]);

  const goToPrevious = () => {
    if (images.length === 0) return;

    setCurrentIndex((previousIndex) =>
      previousIndex === 0 ? images.length - 1 : previousIndex - 1
    );
  };

  const goToNext = () => {
    if (images.length === 0) return;

    setCurrentIndex(
      (previousIndex) => (previousIndex + 1) % images.length
    );
  };

  const goToImage = (index) => {
    setCurrentIndex(index);
  };

  const getVisibleThumbnailIndexes = () => {
    if (images.length <= 7) {
      return images.map((_, index) => index);
    }

    const indexes = [];

    for (let offset = -3; offset <= 3; offset += 1) {
      let index = currentIndex + offset;

      if (index < 0) {
        index = images.length + index;
      }

      if (index >= images.length) {
        index -= images.length;
      }

      indexes.push(index);
    }

    return indexes;
  };

  const visibleThumbnailIndexes = getVisibleThumbnailIndexes();

  if (images.length === 0) {
    return (
      <main className="flex h-screen items-center justify-center overflow-hidden bg-[#F4F7FC] px-6 text-gray-900 dark:bg-[#141414] dark:text-white">
        <div className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.32em] text-[#CDB937]">
            EthioCare
          </p>

          <h1 className="mt-4 text-4xl font-bold text-[#0B3D91] dark:text-white md:text-6xl">
            Photo Gallery
          </h1>

          <p className="mt-6 text-gray-500 dark:text-gray-400">
            No gallery images were found.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main
      ref={ref}
      className="relative h-screen overflow-hidden bg-[#F4F7FC] text-gray-900 transition-colors duration-300 dark:bg-[#141414] dark:text-white"
    >
      {/* Soft background colour */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-[12%] top-[5%] h-[520px] w-[520px] rounded-full bg-[#0B3D91]/[0.07] blur-[110px] dark:bg-[#0B3D91]/20" />

        <div className="absolute -right-[10%] bottom-[-12%] h-[480px] w-[480px] rounded-full bg-[#CDB937]/[0.10] blur-[120px] dark:bg-[#CDB937]/10" />

        <div className="absolute left-[42%] top-[40%] h-[300px] w-[300px] rounded-full bg-[#0B3D91]/[0.04] blur-[100px]" />
      </div>

      <motion.section
        initial={{ opacity: 0, y: 24 }}
        animate={
          inView
            ? { opacity: 1, y: 0 }
            : { opacity: 0, y: 24 }
        }
        transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="relative z-10 mx-auto flex h-full w-full max-w-[1500px] flex-col px-4 pb-4 pt-24 sm:px-6 sm:pb-5 lg:px-8 lg:pt-24"
      >
        {/* Compact Header */}
        <header className="flex flex-shrink-0 items-end justify-between pb-4">
          <div>
            <div className="mb-2 flex items-center gap-3">
              <span className="h-px w-8 bg-[#CDB937]" />

              <span className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#CDB937] sm:text-xs">
                Our Community
              </span>
            </div>

            <h1 className="text-3xl font-bold tracking-tight text-[#0B3D91] dark:text-white sm:text-4xl lg:text-5xl">
              Photo Gallery
            </h1>
          </div>

          <div className="hidden text-right sm:block">
            <span className="text-2xl font-bold text-[#0B3D91] dark:text-white">
              {String(currentIndex + 1).padStart(2, "0")}
            </span>

            <span className="mx-2 text-gray-300 dark:text-white/20">
              /
            </span>

            <span className="text-sm font-semibold text-gray-400 dark:text-gray-500">
              {String(images.length).padStart(2, "0")}
            </span>
          </div>
        </header>

        {/* Main Gallery */}
        <motion.div
          initial={{ opacity: 0, scale: 0.985 }}
          animate={
            inView
              ? { opacity: 1, scale: 1 }
              : { opacity: 0, scale: 0.985 }
          }
          transition={{
            duration: 0.7,
            delay: 0.1,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="min-h-0 flex-1"
        >
          <div
            className="group relative h-full overflow-hidden rounded-2xl border border-[#0B3D91]/10 bg-[#0B3D91]/5 shadow-[0_22px_65px_rgba(11,61,145,0.14)] dark:border-white/10 dark:bg-[#0d0d0d] dark:shadow-[0_25px_70px_rgba(0,0,0,0.5)]"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={images[currentIndex].src}
                src={images[currentIndex].src}
                alt={`EthioCare gallery ${currentIndex + 1}`}
                initial={{
                  opacity: 0,
                  scale: 1.025,
                }}
                animate={{
                  opacity: 1,
                  scale: 1,
                }}
                exit={{
                  opacity: 0,
                }}
                transition={{
                  duration: 0.65,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </AnimatePresence>

            {/* Subtle image treatment */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-black/[0.04]" />

            {/* Mobile Counter */}
            <div className="absolute right-4 top-4 flex items-center rounded-full border border-white/20 bg-black/30 px-3 py-1.5 text-[10px] font-semibold tracking-wider text-white backdrop-blur-md sm:hidden">
              {String(currentIndex + 1).padStart(2, "0")}

              <span className="mx-2 text-white/40">/</span>

              {String(images.length).padStart(2, "0")}
            </div>

            {/* Previous */}
            <button
              type="button"
              onClick={goToPrevious}
              aria-label="Previous photo"
              className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-black/30 text-3xl font-light text-white backdrop-blur-md transition duration-300 hover:scale-105 hover:border-[#CDB937]/60 hover:bg-[#0B3D91]/90 focus:outline-none focus:ring-2 focus:ring-[#CDB937] sm:left-5 sm:h-13 sm:w-13"
            >
              <span className="-translate-y-[2px]">‹</span>
            </button>

            {/* Next */}
            <button
              type="button"
              onClick={goToNext}
              aria-label="Next photo"
              className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-black/30 text-3xl font-light text-white backdrop-blur-md transition duration-300 hover:scale-105 hover:border-[#CDB937]/60 hover:bg-[#0B3D91]/90 focus:outline-none focus:ring-2 focus:ring-[#CDB937] sm:right-5 sm:h-13 sm:w-13"
            >
              <span className="-translate-y-[2px]">›</span>
            </button>

            {/* Progress Bar */}
            <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/20">
              <motion.div
                key={`progress-${currentIndex}-${isPaused}`}
                initial={{
                  width: "0%",
                }}
                animate={{
                  width: isPaused ? "0%" : "100%",
                }}
                transition={{
                  duration: isPaused ? 0 : 5,
                  ease: "linear",
                }}
                className="h-full bg-[#CDB937]"
              />
            </div>
          </div>
        </motion.div>

        {/* Compact Thumbnail Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={
            inView
              ? { opacity: 1, y: 0 }
              : { opacity: 0, y: 12 }
          }
          transition={{
            duration: 0.5,
            delay: 0.25,
          }}
          className="flex h-[80px] flex-shrink-0 items-end justify-center pt-3"
        >
          <div className="flex items-center justify-center gap-2 sm:gap-3">
            {visibleThumbnailIndexes.map((imageIndex) => {
              const image = images[imageIndex];
              const isActive = currentIndex === imageIndex;

              return (
                <button
                  key={`${image.name}-${imageIndex}`}
                  type="button"
                  onClick={() => goToImage(imageIndex)}
                  aria-label={`View gallery photo ${imageIndex + 1}`}
                  className={`relative overflow-hidden rounded-md transition-all duration-300 ${
                    isActive
                      ? "h-14 w-20 scale-105 border-2 border-[#CDB937] opacity-100 shadow-[0_8px_25px_rgba(205,185,55,0.25)] sm:h-16 sm:w-24"
                      : "h-11 w-14 border border-[#0B3D91]/10 opacity-40 hover:scale-105 hover:opacity-80 dark:border-white/10 sm:h-12 sm:w-20"
                  }`}
                >
                  <img
                    src={image.src}
                    alt=""
                    className="h-full w-full object-cover"
                  />

                  {!isActive && (
                    <div className="absolute inset-0 bg-[#0B3D91]/10" />
                  )}
                </button>
              );
            })}
          </div>
        </motion.div>
      </motion.section>
    </main>
  );
};

export default PhotoGallery;